import { useEffect, useState } from "react";
import { useModals } from "../components/common/ModalsProvider.js";
import { BankCard, BankShell, bankInputClass } from "../components/bank/Shell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";
import { FILTER_INPUT } from "../components/common/FilterBar.js";

function buildCategoryLabels(t: ReturnType<typeof useT>) {
  return {
    revenue: t("bankCategories.categoryRevenue"),
    supplier_payment: t("bankCategories.categorySupplierPayment"),
    tax: t("bankCategories.categoryTax"),
    salary: t("bankCategories.categorySalary"),
    bank_fee: t("bankCategories.categoryBankFee"),
    transfer: t("bankCategories.categoryTransfer"),
    other: t("bankCategories.categoryOther"),
    locations_charges_locatives: t("bankCategories.categoryRent"),
    primes_assurance: t("bankCategories.categoryInsurance"),
    deplacements_reception: t("bankCategories.categoryTravel"),
    fournitures_consommables: t("bankCategories.categorySupplies"),
    intermediaires_honoraires: t("bankCategories.categoryIntermediaryFees"),
    vat_payment: t("bankCategories.categoryVatPayment"),
    personal: t("bankCategories.categoryPersonal"),
    partner_contribution: t("bankCategories.categoryPartnerContribution"),
    partner_reimbursement: t("bankCategories.categoryPartnerReimbursement"),
  } as const satisfies Record<string, string>;
}

function buildMatchFieldLabels(t: ReturnType<typeof useT>) {
  return {
    description: t("bankCategories.matchFieldDescription"),
    raw_description: t("bankCategories.matchFieldRawDescription"),
    amount: t("bankCategories.matchFieldAmount"),
    both: t("bankCategories.matchFieldBoth"),
  } as const satisfies Record<string, string>;
}

const MATCH_TYPE_LABELS = {
  contains: "contains",
  starts_with: "starts_with",
  exact: "exact",
  regex: "regex",
  amount_range: "amount_range",
} as const satisfies Record<string, string>;

type Category = keyof ReturnType<typeof buildCategoryLabels>;
type MatchField = keyof ReturnType<typeof buildMatchFieldLabels>;
type MatchType = keyof typeof MATCH_TYPE_LABELS;

interface RuleForm {
  name: string;
  matchField: MatchField;
  matchType: MatchType;
  matchValue: string;
  amountMin: string;
  amountMax: string;
  category: Category;
  priority: string;
  notes: string;
}

const emptyForm: RuleForm = {
  name: "",
  matchField: "description",
  matchType: "contains",
  matchValue: "",
  amountMin: "",
  amountMax: "",
  category: "other",
  priority: "0",
  notes: "",
};

export default function BankCategorias() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const modals = useModals();
  const t = useT();
  const CATEGORY_LABELS = buildCategoryLabels(t);
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("active");

  const listQuery = trpc.categorizationRules.list.useQuery(
    {
      companyId: companyId ?? 0,
      active: activeFilter === "all" ? undefined : activeFilter === "active",
      limit: 100,
      offset: 0,
    },
    { enabled: companyId !== null },
  );

  const utils = trpc.useUtils();
  const deactivateMutation = trpc.categorizationRules.deactivate.useMutation({
    onSuccess: () => utils.categorizationRules.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("bankCategories.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const applyAllMutation = trpc.categorizationRules.applyAll.useMutation({
    onSuccess: (res) => {
      utils.categorizationRules.invalidate();
      utils.bankTransactions.invalidate();
      void modals.alert({
        message: `${t("bankCategories.appliedPre")}: ${res?.updated ?? 0} ${t("bankCategories.appliedUpdatedSuf")} · ${res?.unchanged ?? 0} ${t("bankCategories.appliedUnchangedSuf")} · ${res?.noMatch ?? 0} ${t("bankCategories.appliedNoMatchSuf")} (${res?.transactionsEligible ?? 0} ${t("bankCategories.appliedEligibleSuf")} · ${res?.rulesApplied ?? 0} ${t("bankCategories.appliedRulesSuf")}).`,
      });
    },
    onError: (e) =>
      void modals.alert({
        title: t("bankCategories.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  const [modal, setModal] = useState<{ mode: "create" } | { mode: "edit"; ruleId: number } | null>(
    null,
  );

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <BankShell subtitle={t("bankCategories.subtitleCategories")}>
        <p className="text-sm text-neutral-400">{t("bankCategories.noCompanyAccess")}</p>
      </BankShell>
    );
  }

  const rules = listQuery.data?.rows ?? [];

  const toolbar = (
    <div className="flex items-center gap-3 flex-wrap">
      <select
        value={companyId ?? ""}
        onChange={(e) => setCompanyId(Number(e.target.value))}
        className={FILTER_INPUT + " w-48"}
      >
        {accessibleCompanies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select
        value={activeFilter}
        onChange={(e) => setActiveFilter(e.target.value as "all" | "active" | "inactive")}
        className={FILTER_INPUT + " w-36"}
      >
        <option value="active">{t("bankCategories.filterActive")}</option>
        <option value="inactive">{t("bankCategories.filterInactive")}</option>
        <option value="all">{t("bankCategories.filterAll")}</option>
      </select>
      <div className="flex-1" />
      {canEdit && (
        <>
          <button
            onClick={() => setModal({ mode: "create" })}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
          >
            {t("bankCategories.newRuleBtn")}
          </button>
          <button
            onClick={async () => {
              const ok = await modals.confirm({
                title: t("bankCategories.applyAllConfirmTitle"),
                message: t("bankCategories.applyAllConfirmMessage"),
                confirmLabel: t("bankCategories.applyAllLabel"),
                tone: "warning",
              });
              if (!ok || !companyId) return;
              applyAllMutation.mutate({ companyId, overwriteAuto: false });
            }}
            disabled={applyAllMutation.isPending}
            className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs disabled:opacity-50"
          >
            {applyAllMutation.isPending
              ? t("bankCategories.applyingBtn")
              : t("bankCategories.applyAllBtn")}
          </button>
        </>
      )}
    </div>
  );

  return (
    <BankShell subtitle={t("bankCategories.subtitle")} toolbar={toolbar}>
      <BankCard
        title={`${rules.length} ${t("bankCategories.rulesCountSuf")}`}
        subtitle={t("bankCategories.rulesOrderHint")}
        padded={false}
      >
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("bankCategories.loading")}</p>
        )}
        {!listQuery.isLoading && rules.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("bankCategories.emptyState")}</p>
        )}
        {rules.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-right px-3 py-2 w-16">{t("bankCategories.colPriority")}</th>
                <th className="text-left px-3 py-2">{t("bankCategories.name")}</th>
                <th className="text-left px-3 py-2 w-32">{t("bankCategories.colField")}</th>
                <th className="text-left px-3 py-2 w-28">{t("bankCategories.colType")}</th>
                <th className="text-left px-3 py-2">{t("bankCategories.colValue")}</th>
                <th className="text-left px-3 py-2 w-28">{t("bankCategories.colCategory")}</th>
                <th className="text-center px-3 py-2 w-16">{t("bankCategories.colActive")}</th>
                {canEdit && (
                  <th className="px-3 py-2 w-32 text-right">{t("bankCategories.colActions")}</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {rules.map((r) => (
                <tr key={r.id} className={r.active ? "" : "opacity-60"}>
                  <td className="px-3 py-2 text-right font-mono text-neutral-400">{r.priority}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{r.matchField}</td>
                  <td className="px-3 py-2 text-xs text-neutral-400">{r.matchType}</td>
                  <td className="px-3 py-2 text-xs font-mono text-neutral-400 truncate max-w-md">
                    {r.matchType === "amount_range"
                      ? `[${r.amountMin ?? "—"}, ${r.amountMax ?? "—"}]`
                      : r.matchValue}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {CATEGORY_LABELS[r.category as Category] ?? r.category}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.active ? (
                      <span className="text-lime">✓</span>
                    ) : (
                      <span className="text-neutral-700">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setModal({ mode: "edit", ruleId: r.id })}
                          className="text-xs text-lime hover:underline"
                        >
                          {t("bankCategories.editBtn")}
                        </button>
                        {r.active && (
                          <button
                            onClick={async () => {
                              const reason = await modals.promptReason({
                                title: `${t("bankCategories.deactivateRulePre")} "${r.name}"`,
                                message: t("bankCategories.deactivateRuleMessage"),
                                placeholder: t("bankCategories.deactivateReasonPlaceholder"),
                                confirmLabel: t("bankCategories.deactivateLabel"),
                                tone: "danger",
                                required: true,
                              });
                              if (!reason || !companyId) return;
                              deactivateMutation.mutate({
                                companyId,
                                ruleId: r.id,
                                reason,
                              });
                            }}
                            className="text-xs text-red-400 hover:underline"
                          >
                            {t("bankCategories.deactivateBtn")}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </BankCard>

      {modal && companyId !== null && (
        <RuleModal
          companyId={companyId}
          mode={modal.mode}
          ruleId={modal.mode === "edit" ? modal.ruleId : undefined}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            utils.categorizationRules.invalidate();
          }}
        />
      )}
    </BankShell>
  );
}

function RuleModal({
  companyId,
  mode,
  ruleId,
  onClose,
  onSaved,
}: {
  companyId: number;
  mode: "create" | "edit";
  ruleId?: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const CATEGORY_LABELS = buildCategoryLabels(t);
  const MATCH_FIELD_LABELS = buildMatchFieldLabels(t);
  const detailQuery = trpc.categorizationRules.getById.useQuery(
    { companyId, ruleId: ruleId ?? 0 },
    {
      enabled: mode === "edit" && !!ruleId,
      retry: false,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  const [form, setForm] = useState<RuleForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit") return;
    const d = detailQuery.data;
    if (!d) return;
    setForm({
      name: d.name,
      matchField: d.matchField as MatchField,
      matchType: d.matchType as MatchType,
      matchValue: d.matchValue ?? "",
      amountMin: d.amountMin ?? "",
      amountMax: d.amountMax ?? "",
      category: d.category as Category,
      priority: String(d.priority),
      notes: d.notes ?? "",
    });
  }, [mode, detailQuery.data?.id, detailQuery.dataUpdatedAt]);

  const utilsLocal = trpc.useUtils();
  const createMutation = trpc.categorizationRules.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });
  const updateMutation = trpc.categorizationRules.update.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });
  const [testing, setTesting] = useState(false);

  const pending = createMutation.isPending || updateMutation.isPending || testing;

  function buildData() {
    const priority = Number(form.priority) || 0;
    const amountMin = form.amountMin.trim() ? Number(form.amountMin.replace(",", ".")) : undefined;
    const amountMax = form.amountMax.trim() ? Number(form.amountMax.replace(",", ".")) : undefined;
    return {
      name: form.name.trim(),
      matchField: form.matchField,
      matchType: form.matchType,
      matchValue: form.matchValue.trim() || undefined,
      amountMin: amountMin !== undefined && Number.isFinite(amountMin) ? amountMin : undefined,
      amountMax: amountMax !== undefined && Number.isFinite(amountMax) ? amountMax : undefined,
      category: form.category,
      priority,
      notes: form.notes.trim() || undefined,
    };
  }

  async function handleTest() {
    setError(null);
    setTestResult(null);
    if (mode !== "edit" || !ruleId) {
      setError(t("bankCategories.testSaveFirstError"));
      return;
    }
    setTesting(true);
    try {
      const res = await utilsLocal.categorizationRules.testRule.fetch({
        companyId,
        ruleId,
      });
      setTestResult(
        `${t("bankCategories.testMatchPre")} ${res?.matched ?? 0} / ${res?.totalEligible ?? 0} ${t("bankCategories.testEligibleSuf")} ${t("bankCategories.testSamplePre")}: ${
          res?.sample
            ?.slice(0, 3)
            .map((s) => s.description ?? "—")
            .join(" · ") ?? t("bankCategories.testSampleNone")
        }`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (mode === "create") {
      createMutation.mutate({ companyId, ...buildData() });
    } else if (ruleId) {
      updateMutation.mutate({
        companyId,
        ruleId,
        patch: buildData(),
      });
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {mode === "create"
              ? t("bankCategories.modalCreateTitle")
              : t("bankCategories.modalEditTitle")}
          </h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-300 text-lg">
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("bankCategories.name")} required>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                maxLength={200}
                disabled={pending}
                className={bankInputClass + " w-full"}
              />
            </Field>
            <Field label={t("bankCategories.labelPriority")}>
              <input
                type="number"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                disabled={pending}
                className={bankInputClass + " w-full font-mono text-right"}
              />
            </Field>
            <Field label={t("bankCategories.labelMatchField")} required>
              <select
                value={form.matchField}
                onChange={(e) => setForm({ ...form, matchField: e.target.value as MatchField })}
                disabled={pending}
                className={bankInputClass + " w-full"}
              >
                {Object.entries(MATCH_FIELD_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("bankCategories.labelMatchType")} required>
              <select
                value={form.matchType}
                onChange={(e) => setForm({ ...form, matchType: e.target.value as MatchType })}
                disabled={pending}
                className={bankInputClass + " w-full"}
              >
                {Object.entries(MATCH_TYPE_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            {form.matchType !== "amount_range" && (
              <Field label={t("bankCategories.labelMatchValue")} required>
                <input
                  value={form.matchValue}
                  onChange={(e) => setForm({ ...form, matchValue: e.target.value })}
                  required
                  maxLength={500}
                  disabled={pending}
                  placeholder={
                    form.matchType === "regex"
                      ? t("bankCategories.placeholderRegexExample")
                      : t("bankCategories.placeholderContainsExample")
                  }
                  className={bankInputClass + " w-full font-mono"}
                />
              </Field>
            )}
            {form.matchType === "amount_range" && (
              <>
                <Field label={t("bankCategories.labelAmountMin")}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.amountMin}
                    onChange={(e) => setForm({ ...form, amountMin: e.target.value })}
                    disabled={pending}
                    className={bankInputClass + " w-full font-mono text-right"}
                  />
                </Field>
                <Field label={t("bankCategories.labelAmountMax")}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.amountMax}
                    onChange={(e) => setForm({ ...form, amountMax: e.target.value })}
                    disabled={pending}
                    className={bankInputClass + " w-full font-mono text-right"}
                  />
                </Field>
              </>
            )}
            <Field label={t("bankCategories.labelCategoryResult")} required>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value as Category })}
                disabled={pending}
                className={bankInputClass + " w-full"}
              >
                {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={t("bankCategories.labelNotes")}>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={2000}
              rows={2}
              disabled={pending}
              className={bankInputClass + " w-full resize-y"}
            />
          </Field>

          {testResult && (
            <div className="text-sm text-sky-300 bg-sky-500/10 border border-sky-500/30 rounded-md px-3 py-2">
              {testResult}
            </div>
          )}
          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-between gap-2 pt-2 border-t border-neutral-800">
            <button
              type="button"
              onClick={handleTest}
              disabled={pending || mode === "create"}
              title={
                mode === "create"
                  ? t("bankCategories.testSaveFirstTitle")
                  : t("bankCategories.testRunsTitle")
              }
              className="rounded-md border border-sky-500/40 hover:border-sky-500 disabled:opacity-40 text-sky-300 px-3 py-1.5 text-xs"
            >
              {testing ? t("bankCategories.testingBtn") : t("bankCategories.testRuleBtn")}
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={pending}
                className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
              >
                {t("bankCategories.cancelBtn")}
              </button>
              <button
                type="submit"
                disabled={pending}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
              >
                {pending
                  ? t("bankCategories.savingBtn")
                  : mode === "create"
                    ? t("bankCategories.createBtn")
                    : t("bankCategories.saveBtn")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}
